import React, { useState, useMemo, useEffect, FC } from "react";
import { Container, ConnectedAlert, ProgressBar } from "../../../components";
import { Text, Button } from "@telia/teddy";
import _cloneDeep from "lodash/cloneDeep";
import _find from "lodash/find";
import _get from "lodash/get";
import _set from "lodash/set";
import { connect, useDispatch } from "react-redux";
import { getStateForGAFromManualOrder } from "./utils";
import { FORM_ID } from "../../../../../constants";
import { Discount, Form, RootState } from "../../../../../models";
import { BusinessProduct, OfferingB2B } from "../../../../../models/state";
import { getFormattedPrice } from "../../../../../shared/priceFormatter";
import { getOrderId } from "../../../app/state/selectors";
import { trackTransaction } from "../../../google-analytics";
import { selectors as LOADING_SELECTORS } from "../../../loading";
import { RapidForm } from "../../../rapidform";
import { onRemoveField } from "../../../rapidform/state/actions";
import B2BDeliveryCard from "../../components/organisms/B2BDeliveryCard/B2BDeliveryCard";
import B2BOrganizationCard from "../../components/organisms/B2BOrganizationCard/B2BOrganizationCard";
import B2BProductCard from "../../components/organisms/B2BProductCard/B2BProductCard";
import B2BReceiptCard from "../../components/organisms/B2BReceiptCard/B2BReceiptCard";
import { usePrevious } from "../../hooks";
import { submitManualOrder } from "../../state/actions/businessActions";
import { getBusinessProducts, isMbb as isMbbSelector } from "../../state/selectors/businessProductsSelector";
import { getFormById } from "../../state/selectors/formSelectors";
import { getOfferingsForCart, getPreSelectedOffering } from "../../state/selectors/offeringsSelectors";
import * as VERIFICATION_SELECTORS from "../../state/selectors/verificationSelectors";
import * as WEBDEALS_SELECTORS from "../../state/selectors/webdealsSelectors";
import TEXT from "../../text-consts-b2b";
import "./B2BOrder.less";

interface StateProps {
    businessDetailsForm: Form;
    isFetchingData: boolean;
    isMbb: boolean;
    offeringsForCart: OfferingB2B[];
    orderId?: string;
    preSelectedOffering?: string;
    product: Partial<BusinessProduct>;
    successfullySubmittedManualOrder: boolean;
    webdealsInCart: Discount[];
}

type Props = StateProps;

const B2BOrder: FC<Props> = ({
    isFetchingData,
    businessDetailsForm,
    preSelectedOffering,
    offeringsForCart,
    webdealsInCart,
    successfullySubmittedManualOrder,
    isMbb,
    product,
    orderId,
}) => {
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [productUserIds, setProductUserIds] = useState([Date.now()]);
    const [isNewNumbers, setIsNewNumbers] = useState([false]);
    const [wantInsurance, setWantInsurance] = useState(false);
    const [selectedSubscriptions, setSelectedSubscriptions] = useState([
        _find(offeringsForCart, (offering) => offering.offeringCode === preSelectedOffering) || offeringsForCart[0],
    ]);

    const dispatch = useDispatch();
    const [numberOfSubscriptions, setNumberOfSubscriptions] = useState(1);

    const summaryTableItems = useMemo(() => {
        const mapped = selectedSubscriptions
            .filter((subscription) => subscription && subscription.name)
            .map((subscription) => ({
                key: subscription.name,
                value: `${getFormattedPrice(subscription.monthlyPrice)}/md`,
                monthlyPrice: subscription.monthlyPrice,
            }));

        if (product && product.sku && typeof product.salesPriceWithoutVat !== "undefined") {
            const summaryTableProduct = {
                key: product.name,
                value: isMbb ? `${product.salesPriceWithoutVat},-` : `${Math.round(product.salesPriceWithoutVat)},-/md`,
                monthlyPrice: isMbb ? 0 : product.salesPriceWithoutVat,
            };
            return [...mapped, summaryTableProduct];
        }

        return mapped;
    }, [isMbb, product, selectedSubscriptions]);

    const totalMonthlyPrice = useMemo(
        () => summaryTableItems.reduce((a, b) => +a + +b.monthlyPrice, 0),
        [summaryTableItems],
    );

    // oxlint-disable-next-line prefer-spread
    const list = Array.apply(null, new Array(numberOfSubscriptions)).map(Number.call, Number) as number[];
    const email = _get(businessDetailsForm, "values.manualOrder.email", "");

    const toggleInsurance = () => {
        setWantInsurance((prev) => !prev);
    };

    const formatName = (name: string, price: string) => {
        return name + ` (${price + ",-/md"})`;
    };

    const updateIsNewNumber = (value: boolean, index: number) => {
        const clonedIsNewNumber = _cloneDeep(isNewNumbers);
        const updatedIsNewNumber = _set(clonedIsNewNumber, `${[index]}`, value);
        setIsNewNumbers(updatedIsNewNumber);
    };

    const updateSelectedSubscription = (subscription: OfferingB2B, index: number) => {
        const clonedSelectedSubscription = _cloneDeep(selectedSubscriptions);
        const updatedSubscription = _set(clonedSelectedSubscription, `${[index]}`, subscription);
        setSelectedSubscriptions(updatedSubscription);
    };

    const prevSuccessfullySubmittedManualOrder = usePrevious(successfullySubmittedManualOrder);
    useEffect(() => {
        if (
            successfullySubmittedManualOrder &&
            successfullySubmittedManualOrder !== prevSuccessfullySubmittedManualOrder
        ) {
            setIsSubmitted(true);
            if (product) {
                trackTransaction(getStateForGAFromManualOrder(selectedSubscriptions, productUserIds, product, orderId));
            }
        }
    }, [
        successfullySubmittedManualOrder,
        prevSuccessfullySubmittedManualOrder,
        selectedSubscriptions,
        productUserIds,
        product,
        orderId,
    ]);

    const addProduct = () => {
        setSelectedSubscriptions([...selectedSubscriptions, offeringsForCart[0]]);
        setIsNewNumbers([...isNewNumbers, false]);
        setProductUserIds([...productUserIds, Date.now()]);
        setNumberOfSubscriptions(numberOfSubscriptions + 1);
    };

    const deleteProduct = (index: number) => {
        const clonedSelectedSubscription = _cloneDeep(selectedSubscriptions);
        clonedSelectedSubscription.splice(index, 1);
        const clonedIsNewNumber = _cloneDeep(isNewNumbers);
        clonedIsNewNumber.splice(index, 1);
        const clonedProductUserIds = _cloneDeep(productUserIds);
        const [deletedId] = clonedProductUserIds.splice(index, 1);
        setSelectedSubscriptions(clonedSelectedSubscription);
        setIsNewNumbers(clonedIsNewNumber);
        setProductUserIds(clonedProductUserIds);
        setNumberOfSubscriptions(numberOfSubscriptions - 1);
        dispatch(onRemoveField(FORM_ID.BUSINESS_DETAILS, "productUser_" + deletedId));
    };

    if (isSubmitted) {
        return <B2BReceiptCard email={email} />;
    }

    return (
        <div className="b2b-order">
            <ProgressBar isFetchingData={isFetchingData} standByColor="white" />
            <ConnectedAlert />
            <RapidForm
                id={FORM_ID.BUSINESS_DETAILS}
                action={"#"}
                errors={businessDetailsForm.errors}
                values={businessDetailsForm.values}
                visible={businessDetailsForm.visible}
                started={businessDetailsForm.started}
                onSubmit={() =>
                    dispatch(
                        submitManualOrder({
                            ids: productUserIds,
                            isNewNumbers,
                            selectedSubscriptions,
                            numberOfSubscriptions,
                            product,
                            wantInsurance,
                            webdealsInCart,
                        }),
                    )
                }
            >
                <B2BOrganizationCard />
                <B2BProductCard
                    ids={productUserIds}
                    updateIsNewNumber={updateIsNewNumber}
                    toggleInsurance={toggleInsurance}
                    formatName={formatName}
                    wantInsurance={wantInsurance}
                    isNewNumbers={isNewNumbers}
                    product={product}
                    updateSelectedSubscription={updateSelectedSubscription}
                    selectedSubscriptions={selectedSubscriptions}
                    offeringsForCart={offeringsForCart}
                    addProduct={addProduct}
                    list={list}
                    webdealsInCart={webdealsInCart}
                    deleteProduct={deleteProduct}
                />
                <B2BDeliveryCard
                    isMbb={isMbb}
                    tableItems={summaryTableItems}
                    totalUpfrontPrice={product.salesPriceWithoutVat}
                    totalMonthlyPrice={totalMonthlyPrice}
                />
                <Container size="large" className="b2b-order__order-button-container">
                    <Text mb="100" variant="additional-100">
                        {TEXT.B2B_MANUAL_ORDER.SUBMIT.INGRESS}
                    </Text>
                    <Button
                        variant="primary"
                        disabled={isFetchingData}
                        type="submit"
                        loading={isFetchingData}
                        form={FORM_ID.BUSINESS_DETAILS}
                    >
                        {TEXT.B2B_MANUAL_ORDER.SUBMIT.BUTTON}
                    </Button>
                </Container>
            </RapidForm>
        </div>
    );
};

const mapStateToProps: (state: RootState) => StateProps = (state) => ({
    isFetchingData: LOADING_SELECTORS.isLoading(state),
    preSelectedOffering: getPreSelectedOffering(state),
    offeringsForCart: getOfferingsForCart(state) as OfferingB2B[],
    webdealsInCart: WEBDEALS_SELECTORS.getWebdealsInCart(state),
    successfullySubmittedManualOrder: VERIFICATION_SELECTORS.successfullySubmittedManualOrder(state),
    isMbb: isMbbSelector(state),
    product: getBusinessProducts(state),
    orderId: getOrderId(state),
    // Form data
    businessDetailsForm: getFormById(state, FORM_ID.BUSINESS_DETAILS),
});

export default connect(mapStateToProps)(B2BOrder);
